import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import '../styles/google-maps.css';

interface GoogleMapOverlayProps {
  latitude: number[];
  longitude: number[];
  width: number;
  height: number;
  children: React.ReactNode; // SVG overlay content
  onMapReady?: (convertGPSToPixel: (lat: number, lng: number) => { x: number; y: number }) => void;
  onMapClick?: () => void; // Handle clicks on the map background
  onZoomToSection?: (zoomFunction: (bounds: google.maps.LatLngBounds) => Promise<void>) => void; // Provide zoom function
  onResetToOriginalView?: (resetFunction: () => void) => void; // Provide reset function
  onOriginalStateCaptured?: () => void; // Notify when original state is captured and interactions are safe
  onActualDimensions?: (width: number, height: number) => void; // Report actual container dimensions
  onMapTransitionStart?: () => void;
  onMapTransitionEnd?: () => void;
}

const GoogleMapOverlay: React.FC<GoogleMapOverlayProps> = ({
  latitude,
  longitude,
  width,
  height,
  children,
  onMapReady,
  onMapClick,
  onZoomToSection,
  onResetToOriginalView,
  onOriginalStateCaptured,
  onActualDimensions,
  onMapTransitionStart,
  onMapTransitionEnd
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [mapViewVersion, setMapViewVersion] = useState(0); // Trigger coordinate updates
  const overlayRef = useRef<google.maps.OverlayView | null>(null);

  useEffect(() => {
    const initMap = async () => {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      if (!apiKey) {
        console.error('Google Maps API key not found');
        return;
      }

      const loader = new Loader({
        apiKey,
        version: 'weekly',
      });

      try {
        await loader.load();
        
        if (!mapRef.current || latitude.length === 0 || longitude.length === 0) return;

        // Calculate bounds from track data
        const bounds = new google.maps.LatLngBounds();
        
        for (let i = 0; i < latitude.length; i++) {
          const latLng = new google.maps.LatLng(latitude[i], longitude[i]);
          bounds.extend(latLng);
        }
        

        // Create map with explicit gesture handling (no minZoom initially)
        const mapInstance = new google.maps.Map(mapRef.current, {
          mapTypeId: google.maps.MapTypeId.SATELLITE,
          // Minimal UI, focus on functionality
          disableDefaultUI: true,
          zoomControl: false, // Remove +/- zoom buttons
          // Enable direct panning and zooming
          gestureHandling: 'auto',
          // Explicitly enable all zoom methods
          scrollwheel: true,
          keyboardShortcuts: true,
          clickableIcons: false,
          // Ensure dragging works
          draggable: true,
        });

        // Store original state for restoration
        let originalZoom: number;
        let originalCenter: google.maps.LatLng;
        let isOriginalStateCaptured = false;
        const maxZoomMultiplier = 50; // Shared zoom limit multiplier

        // Create native Google Maps OverlayView for precise coordinate conversion
        const createOverlayView = () => {
          const overlay = new google.maps.OverlayView();
          let hasLoggedSizes = false;
          let isFullyReady = false;
          
          overlay.onAdd = function() {
            // OverlayView is now ready for coordinate conversion
          };
          
          overlay.draw = function() {
            // Ensure everything is fully initialized before proceeding
            const projection = overlay.getProjection();
            if (!projection) {
              return;
            }
            
            // Check for container size mismatches and update dimensions if needed (log once)
            if (!hasLoggedSizes) {
              const mapDiv = mapInstance.getDiv();
              if (mapDiv) {
                const mapRect = mapDiv.getBoundingClientRect();
                const actualWidth = mapDiv.clientWidth || mapRect.width;
                const actualHeight = mapDiv.clientHeight || mapRect.height;
                // Report actual dimensions to parent component
                if (onActualDimensions) {
                  onActualDimensions(Math.round(actualWidth), Math.round(actualHeight));
                }
                hasLoggedSizes = true;
                
                // Mark as fully ready only after everything is initialized
                setTimeout(() => {
                  isFullyReady = true;
                  setMapViewVersion(prev => prev + 1); // Force coordinate recalculation
                }, 100);
              } else {
                // Check if dimensions changed during zoom operations
                const mapDiv = mapInstance.getDiv();
                if (mapDiv) {
                  const mapRect = mapDiv.getBoundingClientRect();
                  const currentWidth = mapDiv.clientWidth || mapRect.width;
                  const currentHeight = mapDiv.clientHeight || mapRect.height;
                  
                  // Report dimension changes during zoom
                  if (onActualDimensions && (Math.abs(currentWidth - width) > 5 || Math.abs(currentHeight - height) > 5)) {
                    onActualDimensions(Math.round(currentWidth), Math.round(currentHeight));
                  }
                }
              }
            }
            
            // Update coordinate conversion when map changes (only if fully ready)
            if (isFullyReady) {
              setMapViewVersion(prev => prev + 1);
            }
          };
          
          overlay.onRemove = function() {
            // Cleanup if needed
          };
          
          overlay.setMap(mapInstance);
          overlayRef.current = overlay;
          
          return overlay;
        };

        // Create the native overlay for coordinate conversion
        createOverlayView();

        // Fit map to track bounds with some padding for better initial view
        mapInstance.fitBounds(bounds, 20);

        // Disable user interaction during initialization to prevent interference
        mapInstance.setOptions({
          zoomControl: false,
          scrollwheel: false,
          disableDoubleClickZoom: true,
          draggable: false
        });

        // Simple initialization - enable interactions immediately after map loads
        const enableInteractions = () => {
          if (isOriginalStateCaptured) return;
          
          const finalZoom = mapInstance.getZoom();
          const finalCenter = mapInstance.getCenter();
          
          if (finalZoom && finalCenter) {
            originalZoom = finalZoom;
            originalCenter = finalCenter;
            isOriginalStateCaptured = true;
            
            // Enable section interactions immediately
            if (onOriginalStateCaptured) {
              onOriginalStateCaptured();
            }
            
            // Enable user controls with reasonable zoom limits
            const maxZoom = originalZoom + Math.log2(maxZoomMultiplier);
            mapInstance.setOptions({
              zoomControl: false,
              scrollwheel: true,
              disableDoubleClickZoom: false,
              draggable: true,
              maxZoom: maxZoom // Prevent excessive zooming
            });
          }
        };
        
        // Enable interactions as soon as map is ready
        google.maps.event.addListenerOnce(mapInstance, 'idle', enableInteractions);

        setMap(mapInstance);
        
        // Wait for map to be fully rendered before marking as loaded
        google.maps.event.addListenerOnce(mapInstance, 'idle', () => {
          setIsLoaded(true);
        });


        // Simple overlay updates - no precision corrections needed with native coordinate system
        const updateOverlay = () => {
          setMapViewVersion(prev => prev + 1);
        };

        // Listen to map changes to trigger coordinate recalculation
        google.maps.event.addListener(mapInstance, 'zoom_changed', updateOverlay);
        google.maps.event.addListener(mapInstance, 'center_changed', updateOverlay);
        google.maps.event.addListener(mapInstance, 'resize', updateOverlay);
        
        // Add click listener to close popups when clicking on map
        if (onMapClick) {
          google.maps.event.addListener(mapInstance, 'click', onMapClick);
        }

        // Expose zoom to section functionality
        if (onZoomToSection) {
          const zoomToSection = async (bounds: google.maps.LatLngBounds) => {
            return new Promise<void>((resolve) => {
              // Check if we actually need to zoom by comparing current bounds with target bounds
              const currentBounds = mapInstance.getBounds();
              
              let needsZoom = true;
              if (currentBounds) {
                // Check if current bounds are already very similar to target bounds
                const currentNE = currentBounds.getNorthEast();
                const currentSW = currentBounds.getSouthWest();
                const targetNE = bounds.getNorthEast();
                const targetSW = bounds.getSouthWest();
                
                // Calculate the difference in bounds (small threshold means no significant zoom needed)
                const latDiff = Math.abs(currentNE.lat() - targetNE.lat()) + Math.abs(currentSW.lat() - targetSW.lat());
                const lngDiff = Math.abs(currentNE.lng() - targetNE.lng()) + Math.abs(currentSW.lng() - targetSW.lng());
                
                // If bounds are very similar, don't trigger transition
                if (latDiff < 0.001 && lngDiff < 0.001) {
                  needsZoom = false;
                }
              }
              
              if (needsZoom) {
                // Only start transition and zoom if actually needed
                if (onMapTransitionStart) {
                  onMapTransitionStart();
                }
                
                // Perform the zoom
                mapInstance.fitBounds(bounds, 0);
                
                // Wait for zoom to complete with quick fallback
                let transitionEnded = false;
                const endTransition = () => {
                  if (transitionEnded) return;
                  transitionEnded = true;
                  
                  // Check if container dimensions changed after zoom and update SVG viewBox
                  const mapDiv = mapInstance.getDiv();
                  if (mapDiv) {
                    const mapRect = mapDiv.getBoundingClientRect();
                    const currentWidth = mapDiv.clientWidth || mapRect.width;
                    const currentHeight = mapDiv.clientHeight || mapRect.height;
                    
                    // Force dimension update to parent component
                    if (onActualDimensions) {
                      onActualDimensions(Math.round(currentWidth), Math.round(currentHeight));
                    }
                  }
                  
                  setMapViewVersion(prev => prev + 1);
                  
                  // Notify transition end
                  if (onMapTransitionEnd) {
                    onMapTransitionEnd();
                  }
                  
                  resolve();
                };
                
                // Wait for zoom to complete
                google.maps.event.addListenerOnce(mapInstance, 'idle', endTransition);
                
                // Quick fallback - if no idle event within 300ms, end transition
                setTimeout(endTransition, 300);
              } else {
                // No zoom needed - just resolve immediately without any transitions
                resolve();
              }
            });
          };
          
          // Call the callback with the zoom function
          onZoomToSection(zoomToSection);
        }

        // Expose reset to original view functionality
        if (onResetToOriginalView) {
          const resetToOriginalView = () => {
            // Check if original state has been captured yet
            if (!isOriginalStateCaptured || !originalCenter) {
              return;
            }
            
            // Notify that transition is starting
            if (onMapTransitionStart) {
              onMapTransitionStart();
            }
            
            // Simple reset - just go back to original zoom and center
            mapInstance.setZoom(originalZoom);
            mapInstance.setCenter(originalCenter);
            
            // Wait for reset to complete and update dimensions
            let transitionEnded = false;
            const endTransition = () => {
              if (transitionEnded) return;
              transitionEnded = true;
              
              // Check if container dimensions changed after reset and update SVG viewBox
              const mapDiv = mapInstance.getDiv();
              if (mapDiv) {
                const mapRect = mapDiv.getBoundingClientRect();
                const currentWidth = mapDiv.clientWidth || mapRect.width;
                const currentHeight = mapDiv.clientHeight || mapRect.height;
                
                // Force dimension update to parent component
                if (onActualDimensions) {
                  onActualDimensions(Math.round(currentWidth), Math.round(currentHeight));
                }
              }
              
              setMapViewVersion(prev => prev + 1);
              
              // Notify that transition is complete
              if (onMapTransitionEnd) {
                onMapTransitionEnd();
              }
            };
            
            // Wait for reset to complete
            google.maps.event.addListenerOnce(mapInstance, 'idle', endTransition);
            
            // Quick fallback - if no idle event within 300ms, end transition
            setTimeout(endTransition, 300);
          };
          
          onResetToOriginalView(resetToOriginalView);
        }
        
        // Listen to bounds changes for coordinate updates
        let boundsTimeout: NodeJS.Timeout;
        const debouncedBoundsUpdate = () => {
          clearTimeout(boundsTimeout);
          boundsTimeout = setTimeout(updateOverlay, 50);
        };
        
        google.maps.event.addListener(mapInstance, 'bounds_changed', debouncedBoundsUpdate);
        
      } catch (error) {
        console.error('Error loading Google Maps:', error);
      }
    };

    initMap();
    
    // Cleanup function
    return () => {
      if (overlayRef.current) {
        overlayRef.current.setMap(null);
        overlayRef.current = null;
      }
      if (map) {
        // Clean up Google Maps instance
        google.maps.event.clearInstanceListeners(map);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latitude, longitude]);

  // Handle container resize
  useEffect(() => {
    if (!map) return;
    
    const handleResize = () => {
      // Trigger Google Maps resize
      google.maps.event.trigger(map, 'resize');
    };

    // Small delay to ensure container has finished resizing
    const timeoutId = setTimeout(handleResize, 100);
    
    return () => clearTimeout(timeoutId);
  }, [map, width, height]);

  // Convert GPS coordinates to pixel coordinates using native Google Maps OverlayView
  const convertGPSToPixel = useCallback((lat: number, lng: number): { x: number; y: number } => {
    // Validate inputs and ensure everything is ready
    if (!map || !isLoaded || !overlayRef.current || typeof lat !== 'number' || typeof lng !== 'number' || isNaN(lat) || isNaN(lng)) {
      return { x: 0, y: 0 };
    }

    try {
      const overlay = overlayRef.current;
      const projection = overlay.getProjection();
      
      // Ensure projection is fully ready
      if (!projection) {
        return { x: 0, y: 0 };
      }

      const latLng = new google.maps.LatLng(lat, lng);
      
      // Use Google Maps' native coordinate conversion
      const pixelPoint = projection.fromLatLngToContainerPixel(latLng);
      
      // Validate coordinates
      if (!pixelPoint || 
          typeof pixelPoint.x !== 'number' || typeof pixelPoint.y !== 'number' || 
          isNaN(pixelPoint.x) || isNaN(pixelPoint.y)) {
        return { x: 0, y: 0 };
      }

      // Use coordinates directly - no transformations
      const scaledX = pixelPoint.x;
      const scaledY = pixelPoint.y;
      

      // Final validation
      if (isNaN(scaledX) || isNaN(scaledY) || !isFinite(scaledX) || !isFinite(scaledY)) {
        return { x: 0, y: 0 };
      }

      return {
        x: scaledX,
        y: scaledY
      };
    } catch (error) {
      // Coordinate conversion failed silently
      return { x: 0, y: 0 };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, isLoaded, mapViewVersion, width, height, overlayRef.current]); // Include overlay reference

  // Pass coordinate conversion function to parent via callback
  React.useEffect(() => {
    if (isLoaded && map && onMapReady) {
      onMapReady(convertGPSToPixel);
    }
  }, [isLoaded, map, onMapReady, convertGPSToPixel]);

  const childrenWithProps = children;

  return (
    <div 
      className="google-maps-container"
      style={{ 
        position: 'relative', 
        width: '100%', 
        height: '100%',
        margin: 0
      }}
    >
      {/* Google Maps container */}
      <div
        ref={mapRef}
        style={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 1,
        }}
      />
      
      {/* SVG overlay - positioned but doesn't block map interactions */}
      {isLoaded && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none', // Critical: Don't block map interactions
            zIndex: 2 // Above map, below any UI controls
          }}
        >
          {childrenWithProps}
        </div>
      )}
    </div>
  );
};

export default GoogleMapOverlay;
